/**
 * useICD10Search Hook
 *
 * Custom hook for searching ICD-10-CM codes using NLM's Clinical Table Search Service.
 * Uses the public FHIR terminology API for ValueSet expansion.
 * Provides debounced async search with loading and error states.
 */

import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  startTransition,
} from "react";
import { useDebounce } from "@benchmark/ui/hooks";
import { ICD10_CM_SYSTEM } from "@benchmark/core/constants";
import type { ValueSet, ValueSetExpansionContains } from "@medplum/fhirtypes";

/** NLM Clinical Table Search Service FHIR endpoint for ICD-10-CM. @agentic @benchmark/core */
const NLM_ICD10_EXPAND_URL =
  "https://clinicaltables.nlm.nih.gov/fhir/R4/ValueSet/icd10cm/$expand";

/**
 * ICD-10 search result item (code, display, system).
 * @agentic @benchmark/core
 */
export type ICD10SearchResult = {
  /** ICD-10-CM code (e.g., "F41.1") */
  code: string;
  /** Human-readable display name (e.g., "Generalized anxiety disorder") */
  display: string;
  /** Code system URL */
  system: string;
};

/** Config for useICD10Search. @agentic @benchmark/core */
export type UseICD10SearchConfig = {
  /** Whether the search is enabled (default: true) */
  enabled?: boolean;
  /** Maximum number of results to return (default: 20) */
  count?: number;
  /** Minimum characters before searching (default: 2) */
  minChars?: number;
};

/** Result from useICD10Search. @agentic @benchmark/core */
export type UseICD10SearchResult = {
  /** Search results */
  data: ICD10SearchResult[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Clear search results */
  clearData: () => void;
};

/**
 * Fetch ICD-10-CM codes from NLM's Clinical Table Search Service.
 * @agentic @benchmark/core
 */
const fetchICD10Codes = async (
  filter: string,
  count: number,
): Promise<ICD10SearchResult[]> => {
  const params = new URLSearchParams({
    filter,
    count: String(count),
  });

  const response = await fetch(`${NLM_ICD10_EXPAND_URL}?${params.toString()}`);

  if (!response.ok) {
    throw new Error(`ICD-10 search failed: ${response.status}`);
  }

  const valueSet: ValueSet = await response.json();

  return (valueSet.expansion?.contains || []).map(
    (item: ValueSetExpansionContains) => ({
      code: item.code || "",
      display: item.display || item.code || "",
      system: item.system || ICD10_CM_SYSTEM,
    }),
  );
};

/**
 * Hook to search ICD-10-CM codes using NLM's Clinical Table Search Service
 *
 * @param searchQuery - Search query string (code or description)
 * @param config - Optional configuration
 * @returns Search results, loading state, error state, and clear function
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useICD10Search("anxiety");
 * // Returns codes like F41.0, F41.1, F41.8 with their descriptions
 * ```
 * @agentic @benchmark/core
 */
export function useICD10Search(
  searchQuery: string,
  config: UseICD10SearchConfig = {},
): UseICD10SearchResult {
  const { enabled: configEnabled = true, count = 20, minChars = 2 } = config;

  const debouncedQuery = useDebounce(searchQuery, 300);

  const [data, setData] = useState<ICD10SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Determine if search should be enabled
  const enabled = configEnabled && debouncedQuery.trim().length >= minChars;

  // Stabilize query for dependency tracking
  const queryKey = enabled ? debouncedQuery.trim() : "";

  const fetchResults = useCallback(async () => {
    if (!enabled) return;

    startTransition(() => {
      setIsLoading(true);
      setError(null);
    });

    try {
      const results = await fetchICD10Codes(queryKey, count);

      startTransition(() => {
        setData(results);
        setIsLoading(false);
      });
    } catch (err) {
      console.error("ICD-10 search error:", err);
      startTransition(() => {
        setError(err as Error);
        setData([]);
        setIsLoading(false);
      });
    }
  }, [queryKey, enabled, count]);

  // Execute search when dependencies change
  useEffect(() => {
    startTransition(() => {
      fetchResults();
    });
  }, [fetchResults]);

  const clearData = useCallback(() => {
    setData([]);
    setError(null);
  }, []);

  return useMemo(
    () => ({
      data,
      isLoading,
      error,
      clearData,
    }),
    [data, isLoading, error, clearData],
  );
}
